import { Component, OnInit, inject } from "@angular/core";
import {
    MatDialog,
    MatDialogConfig,
    MatDialogRef,
    MatDialogModule,
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { FormsModule, ReactiveFormsModule, FormGroup } from "@angular/forms";
import { FormDialogResult } from "@app/shared/models/form-dialog.result";
import { FormDialogConfig } from "@app/shared/models/form-dialog-config.model";
import { FormControlService } from "@app/shared/services/form-control.service";
import { KeyValuePair } from "@app/shared/models/key-value-pair.model";
import { ConfirmDialogComponent } from '@app/shared/dialog/confirm-dialog/confirm-dialog.component';
import { DynamicFormComponent } from "@app/shared/components/dynamic-form/dynamic-form.component";
import { TranslateModule } from "@ngx-translate/core";

@Component({
    selector: "inspekt-picklist-editor-dialog",
    templateUrl: "./picklist-editor-dialog.component.html",
    styleUrls: ["./picklist-editor-dialog.component.scss"],
    imports: [
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        DynamicFormComponent,
        TranslateModule,
    ],
})
export class PicklistEditorDialogComponent implements OnInit {
    dialogRef = inject<MatDialogRef<PicklistEditorDialogComponent, FormDialogResult>>(MatDialogRef);
    private formControlService = inject(FormControlService);
    private matDialog = inject(MatDialog);

    public dialog: FormDialogConfig;
    public form: FormGroup;
    showAddingPickListOptions = false;
    newOptionDescription = "";
    newOptionEnum = "";
    public filterPickListTxt = "";
    public get pickListOptionNumber() {
        return Object.keys(this.form.value).length;
    }

    public get allowToAddPickListOption(): boolean {
        return (
            this.dialog.showAddButton &&
            !this.newOptionDescription &&
            !this.newOptionEnum &&
            !this.showAddingPickListOptions
        );
    }

    public get allowToCreatePickListOption(): boolean {
        return !this.newOptionDescription || !this.newOptionEnum;
    }

    constructor() {
        const dialogRef = this.dialogRef;

        dialogRef.backdropClick().subscribe((res) => {
            this.cancel();
        });
    }

    ngOnInit(): void {
        this.form = this.formControlService.toFormGroup(this.dialog.fields);
    }

    public ok(): void {
        if (this.newOptionEnum || this.newOptionDescription) {
            this.openConfirmDialog(
                "Changes Not Saved",
                "Would you like to proceed without saving?",
                true
            ).subscribe((result) => {
                if (result) {
                    this.dialog.clickedOk = true;
                    this.dialogRef.close({ ok: true, data: this.form.value });
                }
            });
        } else {
            this.dialog.clickedOk = true;
            this.dialogRef.close({ ok: true, data: this.form.value });
        }
    }

    public cancel() {
        this.openConfirmDialog(
            "Are you sure to cancel?",
            "All changes will be lost",
            true
        ).subscribe((result) => {
            if (result) {
                this.dialog.clickedOk = false;
                this.dialogRef.close({ ok: false, data: null });
            }
        });
    }

    public toggleAddPickListOptionElement() {
        this.showAddingPickListOptions = !this.showAddingPickListOptions;
    }

    public addNewPickListOption() {
        const isExist = this.dialog.fields.some(
            (field) => field.key === this.newOptionEnum
        );

        if (isExist) {
            this.openConfirmDialog(
                "Invalid picklist",
                "Has duplicate picklist value",
                false
            );
            return;
        }

        this.form.value[this.newOptionEnum] = this.newOptionDescription;
        this.showAddingPickListOptions = false;

        const newDialogField = new KeyValuePair();
        newDialogField.key = this.newOptionEnum;
        newDialogField.value = this.newOptionDescription;
        this.dialog.fields.push(newDialogField);

        this.newOptionEnum = "";
        this.newOptionDescription = "";
    }

    public filterPickList(filterTxt: string) {
        if (filterTxt) {
            filterTxt = filterTxt.toLowerCase();

            this.dialog.fields.forEach((field: any) => {
                if (
                    field.key.toLowerCase().includes(filterTxt) ||
                    field.value.toLowerCase().includes(filterTxt)
                ) {
                    field.hidden = false;
                } else {
                    field.hidden = true;
                }
            });
        } else {
            this.dialog.fields.forEach(
                (field: any) => (field.hidden = false)
            );
        }
    }

    private openConfirmDialog(
        title: string,
        content: string,
        showCancelButton: boolean
    ) {
        const config: MatDialogConfig = {
            disableClose: false,
            autoFocus: true,
            hasBackdrop: true,
        };
        const dialogRef = this.matDialog.open(ConfirmDialogComponent, config);
        dialogRef.componentInstance.dialog = {
            title,
            content,
            showCancelButton,
        };
        return dialogRef.afterClosed();
    }
}
